import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { useAuth } from '../../Contexts/AuthContext'
import useAdminAccess from '../../services/hooks/useAdminAccess'
import Loader from '../../Components/GeneralComponents/Loader'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import errorToast from '../../Components/Toasts/ErrorToast'
import successToast from '../../Components/Toasts/SuccessToast'
import DraggableGrid from './components/DraggableGrid'
import DraggableList from './components/DraggableList'
import CreateTrendingTab from './components/CreateTrendingTab'
import classes from './featuredCollectionUpdate.module.css'

// Collection group configurations (order matters for tab display)
const COLLECTION_TYPES = {
  themed: {
    name: 'Themed',
    groupId: 'rdMDqgWUstPnFG3TrR57f2d0yUawZKyw',
    description: 'Main themed collections displayed on the app'
  },
  themed_clean: {
    name: '(Minor) Themed',
    groupId: 'W11dX8ZfYh5DDGET5UAts778ArwdHqf4',
    description: 'Clean version of themed collections'
  },
  discover: {
    name: 'Discovery',
    groupId: 'PG588pMwTE62dKguQ3uThxHdMawurRm2',
    description: 'Collections shown on the Discover page'
  },
  create_trending: {
    // No groupId - this tab is read-only and doesn't use the
    // collection-group API at all (see the query's `enabled` below).
    name: 'Create Trending'
  }
}

// Google Sheets logging config
const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL
const GOOGLE_SHEETS_SECRET = process.env.REACT_APP_GOOGLE_SHEETS_SECRET
const TOP_SLOTS_TO_TRACK = 9

export default function FeaturedCollectionUpdate() {
  const { isInternal } = useAdminAccess()
  const { getCollectionGroupList, updateCollectionGroupList, getCollectionById } = useAPI()
  const { getCurrentUser } = useAuth()
  const queryClient = useQueryClient()

  // Ref to store original collections for comparison when saving
  const originalCollectionsRef = useRef([])

  const [activeTab, setActiveTab] = useState('themed')
  const [collections, setCollections] = useState([])
  const [isEdited, setIsEdited] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'json'
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState(null)
  const [resetKey, setResetKey] = useState(0)
  const [removeIndex, setRemoveIndex] = useState(null)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [pendingTab, setPendingTab] = useState(null)

  const {
    data: collectionData,
    isLoading,
    error: fetchError,
    refetch
  } = useQuery({
    queryKey: ['collectionGroupFull', activeTab],
    queryFn: () => getCollectionGroupList(COLLECTION_TYPES[activeTab].groupId),
    // Create Trending has no groupId and fetches its own data - never call
    // getCollectionGroupList(undefined) for it.
    enabled: isInternal && !!getCollectionGroupList && activeTab !== 'create_trending',
    staleTime: 0,
  })

  const updateMutation = useMutation({
    mutationFn: (listFIds) => {
      return updateCollectionGroupList(COLLECTION_TYPES[activeTab].groupId, listFIds)
    },
    onSuccess: () => {
      successToast('Collection order updated successfully!')

      // Log to Google Sheets if top 9 slots changed
      if (hasTopSlotsChanged(originalCollectionsRef.current, collections)) {
        logToGoogleSheets(collections, COLLECTION_TYPES[activeTab].name)
      }

      queryClient.invalidateQueries(['collectionGroupFull', activeTab])
      setIsEdited(false)
      setShowConfirmModal(false)
    },
    onError: (error) => {
      errorToast(`Failed to update: ${error.message}`)
      setShowConfirmModal(false)
    }
  })

  useEffect(() => {
    if (collectionData?.collections) {
      setCollections(collectionData.collections)
      // Store original collections for comparison when saving
      originalCollectionsRef.current = collectionData.collections
      setIsEdited(false)
      updateJsonFromCollections(collectionData.collections)
    }
  }, [collectionData])

  const updateJsonFromCollections = (cols) => {
    const ids = cols.map(c => c.f_id)
    setJsonText(JSON.stringify(ids, null, 2))
    setJsonError(null)
  }

  // Check if any of the top N slots have changed
  const hasTopSlotsChanged = (oldCollections, newCollections) => {
    for (let i = 0; i < TOP_SLOTS_TO_TRACK; i++) {
      const oldId = oldCollections[i]?.f_id
      const newId = newCollections[i]?.f_id
      if (oldId !== newId) {
        return true
      }
    }
    return false
  }

  // Log collection changes to Google Sheets
  const logToGoogleSheets = async (newCollections, tabName) => {
    const user = getCurrentUser()
    const isDev = process.env.NODE_ENV !== 'production'
    const tabNameWithEnv = isDev ? `${tabName} - dev` : tabName

    try {
      await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors', // Required for Google Apps Script
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          secret: GOOGLE_SHEETS_SECRET,
          timestamp: new Date().toLocaleString('en-US', {
            timeZone: 'America/Los_Angeles',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
          userEmail: user?.email || 'Unknown',
          tab: tabNameWithEnv,
          collections: newCollections.map(c => c.name || c.f_id),
        }),
      })
      console.log('Collection changes logged to Google Sheets')
    } catch (error) {
      // Don't block the save if logging fails
      console.error('Failed to log to Google Sheets:', error)
    }
  }

  const handleReorder = (newOrder) => {
    setCollections(newOrder)
    setIsEdited(true)
    updateJsonFromCollections(newOrder)
  }

  const handleIdChange = async (index, newId) => {
    const existingIndex = collections.findIndex((c, i) => c.f_id === newId && i !== index)
    if (existingIndex !== -1) {
      errorToast(`Duplicate: This collection is already at position ${existingIndex + 1}`)
      return false
    }

    try {
      const newCollectionData = await getCollectionById(newId)
      if (newCollectionData) {
        const updatedCollections = [...collections]
        updatedCollections[index] = {
          ...newCollectionData,
          f_id: newId
        }
        setCollections(updatedCollections)
        setIsEdited(true)
        updateJsonFromCollections(updatedCollections)
        successToast(`Updated to: ${newCollectionData.name || newId}`)
        return true
      }
    } catch (error) {
      errorToast(`Collection not found: ${newId}`)
    }
    return false
  }

  const handleRemoveCollection = (index) => {
    setRemoveIndex(index)
    setShowRemoveModal(true)
  }

  const handleConfirmRemove = () => {
    if (removeIndex !== null) {
      const collection = collections[removeIndex]
      const updatedCollections = collections.filter((_, i) => i !== removeIndex)
      setCollections(updatedCollections)
      setIsEdited(true)
      updateJsonFromCollections(updatedCollections)
      successToast(`Removed: ${collection.name || collection.f_id}`)
    }
    setShowRemoveModal(false)
    setRemoveIndex(null)
  }

  const handleAddCollection = async (newId) => {
    const existingIndex = collections.findIndex(c => c.f_id === newId)
    if (existingIndex !== -1) {
      errorToast(`Duplicate: This collection is already at position ${existingIndex + 1}`)
      return false
    }
    try {
      const newCollectionData = await getCollectionById(newId)
      if (newCollectionData) {
        const updatedCollections = [...collections, { ...newCollectionData, f_id: newId }]
        setCollections(updatedCollections)
        setIsEdited(true)
        updateJsonFromCollections(updatedCollections)
        successToast(`Added: ${newCollectionData.name || newId}`)
        return true
      }
    } catch (error) {
      errorToast(`Collection not found: ${newId}`)
    }
    return false
  }

  const handleJsonChange = (e) => {
    const newValue = e.target.value
    setJsonText(newValue)
    setIsEdited(true)

    try {
      const parsed = JSON.parse(newValue)
      if (!Array.isArray(parsed)) {
        setJsonError('JSON must be an array of collection IDs')
      } else if (!parsed.every(item => typeof item === 'string')) {
        setJsonError('All items must be strings (collection IDs)')
      } else {
        setJsonError(null)
      }
    } catch (err) {
      setJsonError(`Invalid JSON: ${err.message}`)
    }
  }

  const handleSave = () => {
    if (viewMode === 'json' && jsonError) {
      errorToast('Please fix JSON errors before saving')
      return
    }
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    let listFIds
    if (viewMode === 'grid') {
      listFIds = collections.map(c => c.f_id)
    } else {
      try {
        listFIds = JSON.parse(jsonText)
      } catch (err) {
        errorToast('Invalid JSON')
        setShowConfirmModal(false)
        return
      }
    }
    updateMutation.mutate(listFIds)
  }

  const handleReset = () => {
    if (collectionData?.collections) {
      setCollections([...collectionData.collections])
      updateJsonFromCollections(collectionData.collections)
      setIsEdited(false)
      setResetKey(prev => prev + 1)
    }
  }

  if (!isInternal) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.accessDenied}>
          <h1>Access Denied</h1>
          <p>You do not have permission to access this page.</p>
          <p>This page is only available to internal Feels Music employees.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h1>Featured Collection Update</h1>
        <p className={classes.subtitle}>
          Drag and drop to reorder collections, or edit collection IDs directly.
        </p>
      </div>

      <div className={classes.tabRow}>
        <div className={classes.tabContainer}>
          {Object.entries(COLLECTION_TYPES).map(([key, config]) => (
            <button
              key={key}
              className={`${classes.tab} ${activeTab === key ? classes.activeTab : ''}`}
              onClick={() => {
                if (isEdited) {
                  setPendingTab(key)
                } else {
                  setActiveTab(key)
                }
              }}
            >
              {config.name}
            </button>
          ))}
        </div>

        {activeTab !== 'create_trending' && (
          <div className={classes.viewToggle}>
            <button
              className={`${classes.viewBtn} ${viewMode === 'grid' ? classes.viewBtnActive : ''}`}
              onClick={() => setViewMode('grid')}
            >
              Visual {activeTab === 'discover' ? 'List' : 'Grid'}
            </button>
            <button
              className={`${classes.viewBtn} ${viewMode === 'json' ? classes.viewBtnActive : ''}`}
              onClick={() => setViewMode('json')}
            >
              JSON Editor
            </button>
          </div>
        )}
      </div>

      {activeTab !== 'create_trending' && (
        <div className={classes.infoRow}>
          <span className={classes.collectionCount}>{collections.length} collections</span>
          <div className={classes.actionButtons}>
            {isEdited && (
              <span className={classes.unsavedIndicator}>Unsaved changes</span>
            )}
            <ButtonOutline
              text="RESET"
              color="red"
              size="extraSmall"
              onClick={handleReset}
              disable={!isEdited}
            />
            <ButtonOutline
              text={updateMutation.isPending ? "SAVING..." : "SAVE"}
              color="green"
              size="extraSmall"
              onClick={handleSave}
              disable={!isEdited || updateMutation.isPending || (viewMode === 'json' && !!jsonError)}
            />
          </div>
        </div>
      )}

      {activeTab === 'create_trending' ? (
        <CreateTrendingTab />
      ) : isLoading ? (
        <div className={classes.loaderContainer}>
          <Loader isLoading={true} />
        </div>
      ) : fetchError ? (
        <div className={classes.errorBox}>
          <p>Error loading collection data: {fetchError.message}</p>
          <ButtonOutline
            text="Retry"
            color="pink"
            size="small"
            onClick={() => refetch()}
          />
        </div>
      ) : viewMode === 'grid' ? (
        activeTab === 'discover' ? (
          <DraggableList
            key={resetKey}
            collections={collections}
            onReorder={handleReorder}
            onIdChange={handleIdChange}
            onAddCollection={handleAddCollection}
            onRemoveCollection={handleRemoveCollection}
          />
        ) : (
          <DraggableGrid
            key={resetKey}
            collections={collections}
            onReorder={handleReorder}
            onIdChange={handleIdChange}
            onAddCollection={handleAddCollection}
            onRemoveCollection={handleRemoveCollection}
          />
        )
      ) : (
        <div className={classes.editorContainer}>
          <div className={classes.editorHeader}>
            <span className={classes.editorTitle}>Collection IDs (JSON Array)</span>
            <ButtonOutline
              text="Format"
              color="pink"
              size="extraSmall"
              onClick={() => {
                try {
                  const parsed = JSON.parse(jsonText)
                  setJsonText(JSON.stringify(parsed, null, 2))
                  setJsonError(null)
                } catch (err) {
                  setJsonError(`Cannot format: ${err.message}`)
                }
              }}
            />
          </div>
          <textarea
            className={`${classes.jsonEditor} ${jsonError ? classes.jsonEditorError : ''}`}
            value={jsonText}
            onChange={handleJsonChange}
            spellCheck={false}
          />
          {jsonError && (
            <div className={classes.validationError}>{jsonError}</div>
          )}
        </div>
      )}

      <ModalWindow
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        rounded={true}
        disableCloseIcon={false}
      >
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Confirm Update</h2>
          <p>
            Are you sure you want to update the <strong>{COLLECTION_TYPES[activeTab].name}</strong> order?
          </p>
          <p className={classes.modalWarning}>
            This will immediately affect the app. Make sure you have verified the changes.
          </p>
          <div className={classes.modalButtonGroup}>
            <ButtonOutline
              text={updateMutation.isPending ? "UPDATING..." : "YES, UPDATE"}
              color="green"
              size="small"
              onClick={handleConfirmSave}
              disable={updateMutation.isPending}
            />
            <ButtonOutline
              text="CANCEL"
              color="red"
              size="small"
              onClick={() => setShowConfirmModal(false)}
              disable={updateMutation.isPending}
            />
          </div>
        </div>
      </ModalWindow>

      <ModalWindow
        open={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false)
          setRemoveIndex(null)
        }}
        rounded={true}
        disableCloseIcon={false}
      >
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Remove Collection</h2>
          {removeIndex !== null && collections[removeIndex] && (
            <>
              <p>
                Are you sure you want to remove <strong>{collections[removeIndex].name || collections[removeIndex].f_id}</strong> from position {removeIndex + 1}?
              </p>
              <p className={classes.modalSubtext}>
                This will not delete the collection, only remove it from this list.
              </p>
            </>
          )}
          <div className={classes.modalButtonGroup}>
            <ButtonOutline
              text="YES, REMOVE"
              color="red"
              size="small"
              onClick={handleConfirmRemove}
            />
            <ButtonOutline
              text="CANCEL"
              color="pink"
              size="small"
              onClick={() => {
                setShowRemoveModal(false)
                setRemoveIndex(null)
              }}
            />
          </div>
        </div>
      </ModalWindow>

      <ModalWindow
        open={!!pendingTab}
        onClose={() => setPendingTab(null)}
        rounded={true}
        disableCloseIcon={false}
      >
        <div className={classes.modalContent}>
          <h2 className={classes.modalTitle}>Unsaved Changes</h2>
          <p>You have unsaved changes. Switch tabs anyway?</p>
          <div className={classes.modalButtonGroup}>
            <ButtonOutline
              text="YES, SWITCH"
              color="pink"
              size="small"
              onClick={() => {
                setActiveTab(pendingTab)
                setPendingTab(null)
              }}
            />
            <ButtonOutline
              text="CANCEL"
              color="red"
              size="small"
              onClick={() => setPendingTab(null)}
            />
          </div>
        </div>
      </ModalWindow>
    </div>
  )
}
